import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleFill, ExclamationTriangleFill, InfoCircleFill, X } from 'react-bootstrap-icons';
import { useNotifications } from '../contexts/NotificationContext';
import { normalizeNotificationRoute } from '../utils/routes';
import './NotificationCenter.css';

const AUTO_DISMISS_MS = 6000;

function NotificationToasts(): React.ReactElement | null
{
    const { toasts, dismissToast, markRead } = useNotifications();
    const navigate = useNavigate();

    useEffect(() => {
        if (toasts.length === 0) return undefined;
        const timers = toasts.map((toast) =>
            window.setTimeout(() => dismissToast(toast.toastKey), AUTO_DISMISS_MS),
        );
        return () => {
            timers.forEach((handle) => window.clearTimeout(handle));
        };
    }, [toasts, dismissToast]);

    if (toasts.length === 0)
    {
        return null;
    }

    function handleClick(
        toastKey: string,
        link: string | null,
        id: number,
        jobType?: string | null,
        status?: string | null,
        projectId?: string | number | null,
        jobId?: string | number | null,
    ): void
    {
        markRead(id);
        dismissToast(toastKey);
        if (link)
        {
            navigate(normalizeNotificationRoute(link, { jobType, status, projectId, jobId }) || link);
        }
    }

    return (
        <div className="notif-toast-stack" role="region" aria-label="Notification toasts">
            {toasts.map((toast) => {
                const variant =
                    toast.status === 'success'
                        ? 'success'
                        : toast.status === 'failure'
                            ? 'failure'
                            : 'info';
                const Icon =
                    variant === 'success'
                        ? CheckCircleFill
                        : variant === 'failure'
                            ? ExclamationTriangleFill
                            : InfoCircleFill;
                return (
                    <div
                        key={toast.toastKey}
                        className={`notif-toast notif-toast-${variant}`}
                        role="status"
                    >
                        <Icon className="notif-toast-icon" />
                        <button
                            type="button"
                            className="notif-toast-body"
                            onClick={() => handleClick(
                                toast.toastKey,
                                toast.link_path,
                                toast.id,
                                toast.job_type,
                                toast.status,
                                toast.metadata?.project_id as string | number | null | undefined,
                                toast.metadata?.job_id as string | number | null | undefined,
                            )}
                        >
                            <span className="notif-toast-title" title={toast.title}>{toast.title}</span>
                            {toast.summary && (
                                <span className="notif-toast-summary" title={toast.summary}>{toast.summary}</span>
                            )}
                            {toast.link_path && (
                                <span className="notif-toast-link">View details →</span>
                            )}
                        </button>
                        <button
                            type="button"
                            className="notif-toast-close"
                            aria-label="Dismiss notification"
                            onClick={() => dismissToast(toast.toastKey)}
                        >
                            <X size={16} />
                        </button>
                    </div>
                );
            })}
        </div>
    );
}

export default NotificationToasts;
